﻿import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { SafeAreas } from "../../safe_areas.js";
import { LeaderboardScreenPlayerInfo } from "./leaderboard_screen_player_info.js";
import { LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";
import { Spinner } from "./spinner.js";
import { UIScrollableContainerCSS } from "../../common_ui_elements/scrollable_container_css.js";
import { LEADERBOARD_TABS, LeaderboardTab, leaderboardBoard } from "../../../api/leaderboard/leaderboard_boards.js";
import type { Board, BoardRow } from "../../../api/leaderboard/leaderboard_boards.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { LeaderboardProfileDialog } from "../../dialog/leaderboard_profile_dialog.js";

const HEADER_H_DESIGN            = 442;
const HEADER_TOP_GAP_DESIGN      = -80;
const TITLE_Y_DESIGN             = 56;
const TITLE_H_DESIGN             = 116;

// World / Country / Friends tabs sit on the header's bottom edge.
const TAB_W_DESIGN               = 342;
const TAB_H_DESIGN               = 148;
const TABS_BOTTOM_GAP_DESIGN     = 34;
const TAB_LABEL_Y_DESIGN         = 46;
const TAB_LABEL_H_DESIGN         = 60;
const TAB_FONT_DESIGN            = 46;

const CONTENT_TOP_DESIGN         = 392;

const ITEM_W_DESIGN              = 1020;
const ITEM_H_DESIGN              = 150;
const ITEM_GAP_DESIGN            = 14;
const CARD_H_DESIGN              = ITEM_H_DESIGN;
const FLOATING_CARD_RENDER_OVERFLOW_DESIGN = 12;
const FLOATING_CARD_BOTTOM_PAD_DESIGN = 12;
const DOCKED_CARD_Y_OFFSET_DESIGN = 0;
const PINNED_CARD_LIFT_DESIGN    = 18;
// The leaderboard content intentionally extends slightly behind the decorative
// top of the navigation, matching the original 1080x1920 composition.
const LEADERBOARD_NAV_RESERVE_DESIGN = 386;

export class LeaderboardScreen extends UIElement
{
  private events_controller: BasicEventsController | null = null;
  private scroll_container: UIScrollableContainerCSS | null = null;
  private scroll_rows: ScrollRow[] = [];
  private scroll_cards = new Map<number, LeaderboardScreenPlayerInfo>();
  private player_row_configs: LeaderboardScreenPlayerInfoConfig[] = [];

  private floating_user_card: LeaderboardScreenPlayerInfo | null = null;
  private floating_card_clip: HTMLDivElement | null = null;
  private floating_card_div: HTMLDivElement | null = null;
  private floating_card_canvas: HTMLCanvasElement | null = null;
  private floating_card_ctx: CanvasRenderingContext2D | null = null;
  private floating_card_view_y: number | null = null;
  private current_user_config: LeaderboardScreenPlayerInfoConfig | null = null;
  private current_user_natural_y: number = 0;
  private is_leaderboard_loading: boolean = false;
  private loading_spinner: Spinner | null = null;
  private loading_request_id: number = 0;

  // The chosen tab survives leaving and reopening the screen.
  private static last_tab: LeaderboardTab = LeaderboardTab.World;
  private active_tab: LeaderboardTab = LeaderboardScreen.last_tab;
  private tab_backgrounds: UIImage[] = [];

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );
    this.initializeEventsController( this.ctx.canvas );
    this.createScreenBackground();
    this.createHeader();
    this.createScrollContainer();
    this.loadAndCreatePlayerInfo( this.active_tab );
  }

  private createScreenBackground(): void
  {
    const full_x = -Game.safeAreaLeft();
    const full_y = -Game.safeAreaTop();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const full_h = this.rect.getH() + Game.safeAreaTop();
    const background = new RoundedRectangle(
      this.ctx,
      full_x,
      full_y,
      full_w,
      full_h,
      0,
      ColorScheme.leaderboard_screen_bg
    );

    this.addChild( background );
  }

  private createScrollContainer(): void
  {
    const design_clip_h_overflow      = 20;
    const scroll_y = LayoutScaler.yFromDesign( CONTENT_TOP_DESIGN );
    const scroll_margin = LayoutScaler.xFromDesign( 10 );
    const scroll_w = this.rect.getW() - scroll_margin * 2;
    const leaderboard_bottom = this.getLeaderboardBottom()
      + LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );
    const scroll_h = Math.max(
      LayoutScaler.heightFromDesign( ITEM_H_DESIGN * 2 ),
      leaderboard_bottom - scroll_y
    );
    const scroll_x = scroll_margin;

    this.scroll_container = new UIScrollableContainerCSS(
      this.ctx,
      scroll_x,
      scroll_y,
      scroll_w,
      scroll_h,
      {
        scroll_speed: 1,
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 0.8,
        clip_horizontal_overflow: LayoutScaler.widthFromDesign( design_clip_h_overflow ),
        pre_scroll_viewport_shrink: LayoutScaler.heightFromDesign(
          CARD_H_DESIGN - FLOATING_CARD_BOTTOM_PAD_DESIGN
        ),
        pre_scroll_defer_expand_until_external: true,
        window_buffer: 0.5,
      }
    );

    this.scroll_container.setWindowRenderer( ( top, bottom ) => this.renderScrollRows( top, bottom ) );
    this.addChild( this.scroll_container! );
    this.scroll_container.setOnClick( ( p ) => this.openProfileFromScrollPoint( p ) );
    this.scroll_container.updateDOMPosition();
  }

  private createHeader(): void
  {
    const header_x = -Game.safeAreaLeft();
    const header_y = -Game.safeAreaTop() + LayoutScaler.yFromDesign( HEADER_TOP_GAP_DESIGN );
    const header_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const header_h = LayoutScaler.heightFromDesign( HEADER_H_DESIGN ) + Game.safeAreaTop();

    const header = new UINineSliceElement(
      this.ctx,
      header_x,
      header_y,
      header_w,
      header_h,
      ImgID.UI_FriendsHeaderBg,
      "friends_header_tabs_bg"
    );

    this.addChild( header );
    this.createTitle();
    this.createTabs();
  }

  private headerBottom(): number
  {
    return LayoutScaler.yFromDesign( HEADER_TOP_GAP_DESIGN ) + LayoutScaler.heightFromDesign( HEADER_H_DESIGN );
  }

  private createTabs(): void
  {
    const tab_w = LayoutScaler.widthFromDesign( TAB_W_DESIGN );
    const tab_h = LayoutScaler.heightFromDesign( TAB_H_DESIGN );
    const tab_y = this.headerBottom() - LayoutScaler.heightFromDesign( TAB_H_DESIGN + TABS_BOTTOM_GAP_DESIGN );
    const group_x = ( this.rect.getW() - tab_w * LEADERBOARD_TABS.length ) / 2;
    const label_y = LayoutScaler.yFromDesign( TAB_LABEL_Y_DESIGN );
    const label_h = LayoutScaler.heightFromDesign( TAB_LABEL_H_DESIGN );
    const keys: Record<LeaderboardTab, string> = {
      [LeaderboardTab.World]:   "LEADERBOARD.TAB.WORLD",
      [LeaderboardTab.Country]: "LEADERBOARD.TAB.COUNTRY",
      [LeaderboardTab.Friends]: "LEADERBOARD.TAB.FRIENDS",
    };

    this.tab_backgrounds = [];

    LEADERBOARD_TABS.forEach( ( tab_id, i ) =>
    {
      const tab = new UIElementHolder( this.ctx, group_x + i * tab_w, tab_y, tab_w, tab_h, false, false );
      tab.setOnClick( () => this.setActiveTab( tab_id ) );

      const background = new UIImage( this.ctx, 0, 0, tab_w, tab_h, ImgID.UI_LeaderboardTabSide );
      background.setClickable( false );
      tab.addChild( background, true );
      this.tab_backgrounds.push( background );

      const label = new UITextElement( this.ctx, 0, label_y, tab_w, label_h, {
        localization_key: keys[tab_id],
        font_size: LayoutScaler.textSizeFromDesign( TAB_FONT_DESIGN ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.friends_tab_outline,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        ...TextStyleScheme.buttonShadow(),
      } );
      label.setClickable( false );
      tab.addChild( label, true );

      this.addChild( tab );
    } );

    this.syncTabs();
  }

  // The side bitmaps are asymmetric: the inactive one is rounded on the right,
  // the active one on the left, so flipping gives each outer tab its rounded
  // outer corner.
  private syncTabs(): void
  {
    const last = LEADERBOARD_TABS.length - 1;

    this.tab_backgrounds.forEach( ( background, i ) =>
    {
      const is_active = LEADERBOARD_TABS[i] === this.active_tab;
      const is_middle = i > 0 && i < last;

      if ( is_middle )
      {
        background.setImageId( is_active ? ImgID.UI_LeaderboardTabMiddleActive : ImgID.UI_LeaderboardTabMiddle );
        background.setFlipX( false );
        return;
      }

      const rounded_left = i === 0;
      background.setImageId( is_active ? ImgID.UI_LeaderboardTabSideActive : ImgID.UI_LeaderboardTabSide );
      background.setFlipX( rounded_left ? !is_active : is_active );
    } );
  }

  private setActiveTab( tab: LeaderboardTab ): void
  {
    if ( tab === this.active_tab ) return;

    void audioController.playSound( AudioClips.UI_CLICK );
    this.active_tab = tab;
    LeaderboardScreen.last_tab = tab;
    this.syncTabs();

    // Drop the previous board right away so the spinner shows on an empty list.
    this.scroll_rows = [];
    this.scroll_cards.clear();
    this.player_row_configs = [];
    this.current_user_config = null;
    this.destroyFloatingCard();
    this.renderScrollCards( 0 );
    this.forceRedraw();

    void this.loadAndCreatePlayerInfo( tab );
  }

  private createTitle(): void
  {
    const design_text_size  = 85;

    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( TITLE_H_DESIGN );
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = LayoutScaler.yFromDesign( TITLE_Y_DESIGN );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            {
              color: "#FFFBD4",
              offset: 0
            },
            {
              color: "#F2E03B",
              offset: 1
            }
          ]
        }
      }
    );

    const title_1 = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 44 ),
        outline_color: ColorScheme.leaderboard_title_1,
      }
    );

    const title_2 = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 40 ),
        outline_color: ColorScheme.leaderboard_title_2,
      }
    );

    const title_3 = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 30 ),
        outline_color: ColorScheme.leaderboard_title_3,
      }
    );

    const title_4 = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 24 ),
        outline_color: ColorScheme.leaderboard_title_4,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    const title_5 = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.leaderboard_title_5,
        shadow_blur: LayoutScaler.widthFromDesign( 3 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_color: ColorScheme.shop_title_inner_outline
      }
    );

    this.addChild( title_1 );
    this.addChild( title_2 );
    this.addChild( title_3 );
    this.addChild( title_4 );
    this.addChild( title_5 );
    this.addChild( title );
  }

  private getLeaderboardBottom(): number
  {
    return Math.max( 0, this.rect.getH() - LayoutScaler.heightFromDesign( LEADERBOARD_NAV_RESERVE_DESIGN ) );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
    }

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private isBlockedByModal(): boolean
  {
    return DialogManager.isAnyDialogOpened() || OverlayManager.isInteractionBlocked();
  }

  private async loadAndCreatePlayerInfo( tab: LeaderboardTab ): Promise<void>
  {
    const request_id = ++this.loading_request_id;
    const board = leaderboardBoard( tab );
    const cached = board.peek();
    if ( cached )
      this.createPlayerInfo( mapBoardRows( cached ) );

    if ( board.isFresh() )
      return;

    if ( !cached )
    {
      this.showLoadingSpinner();
      this.setLeaderboardLoadingState( true );
    }

    try
    {
      const fresh = await board.fetch();

      if ( request_id !== this.loading_request_id || tab !== this.active_tab ) return;

      if ( !cached || !sameBoard( fresh, cached ) )
        this.createPlayerInfo( mapBoardRows( fresh ) );
    }
    catch ( e )
    {
      console.warn( '[LeaderboardScreen] Failed to load players:', String( e ) );
      if ( !cached )
        this.createPlayerInfo( [] );
    }
    finally
    {
      if ( !cached )
      {
        this.hideLoadingSpinnerForRequest( request_id );
        this.setLeaderboardLoadingState( false );
      }
    }
  }

  private showLoadingSpinner(): void
  {
    if ( this.loading_spinner ) return;

    const design_spinner_size = 120;
    const spinner_size = LayoutScaler.widthFromDesign( design_spinner_size );
    const scroll_rect = this.scroll_container?.getRect();
    const scroll_pos = this.scroll_container?.pixelPosImportant();
    const spinner_x = scroll_rect && scroll_pos
      ? scroll_pos.x + ( scroll_rect.getW() - spinner_size ) / 2
      : this.rect.getW2() - spinner_size / 2;
    const spinner_y = scroll_rect && scroll_pos
      ? scroll_pos.y + ( scroll_rect.getH() - spinner_size ) / 2
      : this.rect.getH2() - spinner_size / 2;

    this.loading_spinner = new Spinner(
      this.ctx,
      spinner_x,
      spinner_y,
      spinner_size,
      spinner_size
    );
    this.addChild( this.loading_spinner );
  }

  private hideLoadingSpinner(): void
  {
    if ( !this.loading_spinner ) return;

    this.removeChild( this.loading_spinner );
    this.loading_spinner = null;
  }

  private setLeaderboardLoadingState( is_loading: boolean ): void
  {
    this.is_leaderboard_loading = is_loading;
    const target = is_loading ? 0 : 1;

    if ( this.scroll_container )
    {
      this.scroll_container.setClickable( !is_loading );
      if ( target === 0 )
        this.scroll_container.hideElement( 1 );
      else
        this.scroll_container.showElement( 1 );
    }

    if ( this.floating_card_clip )
      this.floating_card_clip.style.display = target === 0 ? 'none' : '';

    if ( this.floating_user_card )
    {
      if ( is_loading )
        this.floating_user_card.hideElement( 1 );
      else
        this.floating_user_card.showElement( 1 );
    }

    this.forceRedraw();
  }

  private hideLoadingSpinnerForRequest( request_id: number ): void
  {
    if ( request_id !== this.loading_request_id ) return;
    if ( !this.loading_spinner ) return;
    this.hideLoadingSpinner();
  }

  private createPlayerInfo( players: LeaderboardScreenPlayerInfoConfig[] ): void
  {
    if ( !this.scroll_container ) return;

    const item_w   = Math.min(
      this.scroll_container.getRect().getW(),
      LayoutScaler.widthFromDesign( ITEM_W_DESIGN )
    );
    const item_h   = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const item_x   = ( this.scroll_container.getRect().getW() - item_w ) / 2;
    const item_gap = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const top_pad  = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const current_player = players.find( p => p.is_player ) ?? null;
    const current_player_rating = current_player?.rating ?? null;
    const NEIGHBOR_HIGHLIGHT_MIN_RANK = 50;
    const should_highlight_neighbors = current_player_rating !== null
      && current_player_rating >= NEIGHBOR_HIGHLIGHT_MIN_RANK;

    this.scroll_rows = [];
    this.scroll_cards.clear();
    this.player_row_configs = [];
    this.current_user_config = current_player
      ? { ...current_player, is_player: true, is_near_player: false }
      : null;
    this.current_user_natural_y = Number.POSITIVE_INFINITY;

    let card_index = 0;
    let total_slots = 0;

    const visible_players = [ ...players ].sort( ( a, b ) => a.rating - b.rating );

    visible_players.forEach( ( player ) =>
    {
      const is_near_player = should_highlight_neighbors
        && !player.is_player
        && current_player_rating !== null
        && Math.abs( player.rating - current_player_rating ) <= 2;
      const player_with_style: LeaderboardScreenPlayerInfoConfig = {
        ...player,
        is_near_player: player.is_player ? false : is_near_player,
      };
      const item_y = top_pad + card_index * ( item_h + item_gap );

      this.player_row_configs.push( player_with_style );

      if ( player_with_style.is_player )
      {
        this.current_user_natural_y = item_y;
        card_index++;
        total_slots++;
        return;
      }

      this.scroll_rows.push( { config: player_with_style, x: item_x, y: item_y, w: item_w, h: item_h } );
      card_index++;
      total_slots++;
    } );

    this.destroyFloatingCard();
    this.renderScrollCards( total_slots );
    this.createFloatingCard();
    // A board served from cache (tab switch) arrives outside any loading-state
    // change, so nothing else would mark the screen dirty for the new card.
    this.forceRedraw();
  }

  private createPinnedPlayerCard(): void
  {
    if ( !this.current_user_config || !this.scroll_container ) return;

    const scroll_pos = this.scroll_container.pixelPosImportant();
    const card_w = Math.min(
      this.scroll_container.getRect().getW(),
      LayoutScaler.widthFromDesign( ITEM_W_DESIGN )
    );
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const card_x = scroll_pos.x
      + ( this.scroll_container.getRect().getW() - card_w ) / 2;

    this.floating_user_card = new LeaderboardScreenPlayerInfo(
      this.ctx,
      card_x,
      scroll_pos.y + this.scroll_container.getRect().getH() - card_h
        + LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN ),
      card_w,
      card_h,
      this.current_user_config
    );
    this.floating_user_card.setClickable( false );

    if ( this.is_leaderboard_loading )
      this.floating_user_card.hideElement( 1 );

    this.addChild( this.floating_user_card );
    this.updatePinnedPlayerCardPosition();
  }

  private updatePinnedPlayerCardPosition(): void
  {
    // DOM-backed cards are positioned by updateFloatingCardPosition().
    if ( this.floating_card_div || !this.floating_user_card || !this.scroll_container ) return;

    const scroll_pos = this.scroll_container.pixelPosImportant();
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const fixed_y = scroll_pos.y
      + this.scroll_container.getRect().getH()
      - card_h
      + LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN )
      - LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );
    const natural_y = scroll_pos.y
      + this.current_user_natural_y
      - this.scroll_container.getScrollOffset();
    const item_h = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const docked_y = natural_y - ( card_h - item_h ) / 2;
    const viewport_top_y = scroll_pos.y;
    // The player card has three states: pinned to the bottom before its row
    // enters the viewport, following its natural row while it is visible, and
    // pinned to the top after that row has scrolled past it.
    const target_y = Math.max( viewport_top_y, Math.min( docked_y, fixed_y ) );
    const is_docked_to_bottom = docked_y >= fixed_y;

    if ( !is_docked_to_bottom )
    {
      this.scroll_container.expandPreScrollViewportIfNeeded();
      this.floating_user_card.setRenderClip(
        scroll_pos.x,
        scroll_pos.y,
        this.scroll_container.getRect().getW(),
        this.scroll_container.getRect().getH()
      );
    }
    else
    {
      this.scroll_container.collapsePreScrollViewportIfNeeded();
      this.floating_user_card.clearRenderClip();
    }

    this.floating_user_card.setPixelPos(
      new Vector2(
        scroll_pos.x + ( this.scroll_container.getRect().getW() - this.floating_user_card.getRect().getW() ) / 2,
        target_y
      ),
      true
    );
  }

  private renderScrollCards( total_slots?: number ): void
  {
    if ( !this.scroll_container ) return;

    const n        = total_slots ?? this.scroll_rows.length;
    const item_h   = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const item_gap = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const top_pad  = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const content_h = n > 0
      ? top_pad + n * ( item_h + item_gap )
      : this.scroll_container.getRect().getH();

    this.scroll_container.setContentHeight( content_h );
  }

  private renderScrollRows( top: number, bottom: number ): void
  {
    const margin = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );

    this.scroll_rows.forEach( ( row, index ) =>
    {
      if ( row.y + row.h + margin < top || row.y - margin > bottom )
        return;

      const card = this.scrollCard( row, index );
      card.invalidateSubtree();
      card.render();
    } );
  }

  private scrollCard( row: ScrollRow, index: number ): LeaderboardScreenPlayerInfo
  {
    const cached = this.scroll_cards.get( index );
    if ( cached )
      return cached;

    const card = new LeaderboardScreenPlayerInfo(
      this.scroll_container!.getContentCtx(),
      row.x,
      row.y,
      row.w,
      row.h,
      row.config
    );
    this.scroll_cards.set( index, card );
    return card;
  }

  private destroyFloatingCard(): void
  {
    if ( this.floating_user_card )
      this.removeChild( this.floating_user_card );
    this.floating_user_card    = null;
    this.floating_card_ctx     = null;
    this.floating_card_canvas  = null;
    this.floating_card_div     = null;
    this.floating_card_clip?.remove();
    this.floating_card_clip    = null;
    this.floating_card_view_y  = null;
  }

  private createFloatingCard(): void
  {
    if ( !this.current_user_config || !this.scroll_container ) return;

    const card_w = Math.min(
      this.scroll_container.getRect().getW(),
      LayoutScaler.widthFromDesign( ITEM_W_DESIGN )
    );
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const render_h = card_h + LayoutScaler.heightFromDesign( FLOATING_CARD_RENDER_OVERFLOW_DESIGN );
    const dpr    = Game.getDpr();

    // Clip wrapper mirrors the scroll container's own overflow:hidden clip, so the
    // floating card gets progressively clipped at the same top/bottom line as the
    // regular canvas-rendered cards instead of popping visible/hidden abruptly.
    const clip = document.createElement( 'div' );
    clip.style.cssText = [
      'position:fixed', 'pointer-events:none', 'z-index:11', 'overflow:hidden',
      `width:${card_w}px`,
    ].join( ';' );

    const div = document.createElement( 'div' );
    div.style.cssText = [
      'position:absolute', 'left:0',
      `width:${card_w}px`, `height:${render_h}px`,
    ].join( ';' );

    const canvas = document.createElement( 'canvas' );
    canvas.width  = Math.ceil( card_w * dpr );
    canvas.height = Math.ceil( render_h * dpr );
    canvas.style.cssText = [
      'position:absolute', 'left:0', 'top:0',
      `width:${card_w}px`, `height:${render_h}px`,
    ].join( ';' );

    div.appendChild( canvas );
    clip.appendChild( div );
    // Keep the pinned row outside the scroll clip. It must overlap the panel at
    // both edges, while the regular leaderboard rows remain clipped.
    document.body.appendChild( clip );

    const ctx = canvas.getContext( '2d' )!;
    ctx.scale( dpr, dpr );

    this.floating_card_clip  = clip;
    this.floating_card_div   = div;
    this.floating_card_canvas = canvas;
    this.floating_card_ctx    = ctx;

    // pixel_pos (0,0) в†’ startRender translates to (card_w/2, card_h/2) = canvas center
    this.floating_user_card = new LeaderboardScreenPlayerInfo(
      ctx, 0, 0, card_w, card_h, this.current_user_config
    );

    if ( this.is_leaderboard_loading ) clip.style.display = 'none';

    this.updateFloatingCardPosition();
  }

  private updateFloatingCardPosition(): void
  {
    if ( !this.scroll_container || !this.floating_card_div || !this.floating_card_clip ) return;

    const card_h   = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const inset    = LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN );
    const render_overflow = LayoutScaler.heightFromDesign( FLOATING_CARD_RENDER_OVERFLOW_DESIGN );
    const sc_h     = this.scroll_container.getRect().getH();

    // The decorative player substrate is taller than a regular row and is meant
    // to overlap the bottom edge of the list slightly. Keep that tail below the
    // viewport instead of adding an empty inset above the navigation.
    const fixed_y  = sc_h - card_h + inset
      - LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );

    const scroll_y = this.scroll_container.getScrollOffset();
    const natural_y = this.current_user_natural_y - scroll_y;

    const item_h = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const dock_y = natural_y
      - ( card_h - item_h ) / 2
      - LayoutScaler.heightFromDesign( DOCKED_CARD_Y_OFFSET_DESIGN );

    // Use a symmetric outward overlap at both edges. This gives the top-pinned
    // state the same visual treatment as the original bottom-pinned state.
    const top_y = -inset;
    const target_y = Math.max( top_y, Math.min( dock_y, fixed_y ) );
    const is_docked_to_bottom = dock_y >= fixed_y;

    if ( is_docked_to_bottom )
      this.scroll_container.collapsePreScrollViewportIfNeeded();
    else
      this.scroll_container.expandPreScrollViewportIfNeeded();

    const host_rect = this.scroll_container.getOverlayHost().getBoundingClientRect();
    const host_w = host_rect.width;
    const css_x = ( host_w - this.scroll_container.getRect().getW() ) / 2
      + ( this.scroll_container.getRect().getW() - this.floating_user_card!.getRect().getW() ) / 2;

    this.floating_card_clip.style.left   = `${host_rect.left + css_x}px`;
    this.floating_card_clip.style.top    = `${host_rect.top - inset}px`;
    this.floating_card_clip.style.height = `${sc_h + inset * 2 + render_overflow}px`;

    this.floating_card_div.style.top = `${target_y + inset}px`;
    this.floating_card_view_y = target_y;
  }

  private openProfileFromScrollPoint( p: Vector2 ): void
  {
    if ( OverlayManager.isInteractionBlocked() )
      return;

    if ( !this.scroll_container || this.player_row_configs.length === 0 )
      return;

    const item_h   = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const item_gap = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const top_pad  = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const row_step = item_h + item_gap;

    const sc_pos = this.scroll_container.getAbsolutePosition();
    const view_y = p.y - sc_pos.y;
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const pinned_y = this.floating_card_view_y;

    if ( this.current_user_config && pinned_y !== null && view_y >= pinned_y && view_y <= pinned_y + card_h )
    {
      this.openProfileForConfig( this.current_user_config );
      return;
    }

    const local_y = view_y + this.scroll_container.getScrollOffset();
    const row_index = Math.floor( ( local_y - top_pad ) / row_step );
    const row_y = top_pad + row_index * row_step;

    if ( row_index < 0 || row_index >= this.player_row_configs.length )
      return;

    if ( local_y < row_y || local_y > row_y + item_h )
      return;

    this.openProfileForConfig( this.player_row_configs[row_index] );
  }

  // Profile dialog isn't ported yet вЂ” tapping a row is a no-op for now.
  private openProfileForConfig( config: LeaderboardScreenPlayerInfoConfig ): void
  {
    DialogManager.openDialog( new LeaderboardProfileDialog( config ) );
  }


  public destroy(): void
  {
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
    this.scroll_container?.destroy();
    this.scroll_container = null;
    this.scroll_rows = [];
    this.scroll_cards.clear();
    this.player_row_configs = [];
    this.loading_request_id++;
    this.hideLoadingSpinner();
    this.destroyFloatingCard();
    this.current_user_config    = null;
    this.current_user_natural_y = 0;
  }

  public name(): string
  {
    return "LeaderboardScreen";
  }

  private renderFloatingCard(): void
  {
    if ( !this.floating_user_card || !this.floating_card_ctx || !this.floating_card_canvas ) return;
    const ctx = this.floating_card_ctx;
    const dpr = Game.getDpr();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, this.floating_card_canvas.width, this.floating_card_canvas.height );
    ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
    this.floating_user_card.invalidateSubtree();
    this.floating_user_card.render();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.renderFloatingCard();
    this.startRender();

    this.ctx.save();
    this.ctx.translate( -this.rect.getW2(), -this.rect.getH2() );

    const bg_x = -Game.safeAreaLeft();
    const bg_y = -Game.safeAreaTop();
    const bg_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const bg_h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();
    this.ctx.fillStyle = ColorScheme.leaderboard_screen_bg;
    this.ctx.fillRect( bg_x, bg_y, bg_w, bg_h );

    this.ctx.restore();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    if ( this.scroll_container )
      this.scroll_container.update( now );
    this.updatePinnedPlayerCardPosition();
    this.updateFloatingCardPosition();
  }
}

interface ScrollRow
{
  config: LeaderboardScreenPlayerInfoConfig;
  x: number;
  y: number;
  w: number;
  h: number;
}

function sameBoard( a: Board, b: Board ): boolean
{
  return a.snapshot_at === b.snapshot_at
    && a.fallback_to_world === b.fallback_to_world
    && JSON.stringify( a.rows ) === JSON.stringify( b.rows );
}

function mapBoardRows( board: Board ): LeaderboardScreenPlayerInfoConfig[]
{
  const local_fb_url = localStorage.getItem( 'player_facebook_avatar_url' );
  const parsed_local_avatar = parseInt( localStorage.getItem( 'player_avatar_id' ) ?? '0', 10 );
  const local_avatar_id = isNaN( parsed_local_avatar ) ? 0 : parsed_local_avatar;
  return board.rows.map( ( row: BoardRow ) => ( {
    is_player: row.is_player,
    name:      row.name || 'Player',
    level:     row.level ?? 1,
    rating:    row.rank,
    // Own row reflects the local avatar choice (incl. FB id 987) immediately,
    // even before the server echoes it back.
    avatar_id: row.is_player ? local_avatar_id : ( row.avatar_id ?? 0 ),
    facebook_av_url: row.is_player ? local_fb_url : ( row.facebook_avatar_url ?? null ),
    score:     row.score,
    country_code: row.country,
    user_id: String( row.id ),
    days_in_game: daysInGameFromCreatedAt( row.created_at ),
  } ) );
}

function daysInGameFromCreatedAt( created_at: string | null | undefined ): number | null
{
  if ( !created_at ) return null;

  const t = new Date( created_at ).getTime();
  if ( isNaN( t ) ) return null;

  return Math.max( 0, Math.floor( ( Date.now() - t ) / 86_400_000 ) );
}
